import type {
  AgeLimitRestrictionT,
  CoverageMessageT,
  CoverageResourceT,
  CoverageRestrictionsT,
  FormularyResponseT,
  PayerCopayT,
  PayerInformationT,
  PharmacyEligibilityT,
  QuantityLimitRestrictionT,
  StepMedicationRestrictionT,
} from '@lotushealth/lotushealth_js_sdk/schemas';
import {
  AlertCircle,
  Calendar,
  ExternalLink,
  FileText,
  ListTree,
  MapPin,
  MessageSquare,
  ShieldCheck,
  Store,
  User,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { cn } from '@/lib/utils';

import { describeCopay, pharmacyTypeLabel } from '../model/formularyDisplayPolicy';

export interface FormularyDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  restrictions: CoverageRestrictionsT | null | undefined;
  copays: PayerCopayT[] | null | undefined;
  payerInformation: PayerInformationT | null | undefined;
}

const TITLE_ID = 'formulary-detail-dialog-title';
const DESCRIPTION_ID = 'formulary-detail-dialog-description';

export function FormularyDetailDialog({
  open,
  onOpenChange,
  restrictions,
  copays,
  payerInformation,
}: FormularyDetailDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-labelledby={TITLE_ID}
        aria-describedby={DESCRIPTION_ID}
        className="sm:max-w-2xl"
      >
        <DialogHeader>
          <DialogTitle id={TITLE_ID}>Coverage details</DialogTitle>
          <DialogDescription id={DESCRIPTION_ID}>
            Full restrictions, copay, and payer information from the formulary response.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[65vh] space-y-5 overflow-y-auto pr-1">
          <RestrictionsDetail restrictions={restrictions} />
          <CopaysDetail copays={copays} />
          <PayerDetail payer={payerInformation} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/*  Restrictions                                                      */
/* ------------------------------------------------------------------ */

function RestrictionsDetail({
  restrictions,
}: {
  restrictions: CoverageRestrictionsT | null | undefined;
}) {
  if (!restrictions) {
    return (
      <EmptySection icon={AlertCircle} title="Restrictions">
        No restriction data available.
      </EmptySection>
    );
  }

  const booleanFlags: Array<{ label: string; value: boolean | null | undefined }> = [
    { label: 'Excluded', value: restrictions.IsExcluded },
    { label: 'Medical Necessity', value: restrictions.HasMedicalNecessity },
    { label: 'Prior Authorization', value: restrictions.HasPriorAuthorization },
    { label: 'Step Therapy', value: restrictions.HasStepTherapy },
    { label: 'Quantity Limit', value: restrictions.HasQuantityLimit },
    { label: 'Age Limit', value: restrictions.HasAgeLimit },
  ];

  const stepMeds = restrictions.StepMedicationRestrictions ?? [];
  const quantityLimits = restrictions.QuantityLimitRestrictions ?? [];
  const ageLimits = restrictions.AgeLimitRestrictions ?? [];
  const messages = restrictions.Messages ?? [];
  const drugResources = restrictions.DrugResources ?? [];
  const summaryResources = restrictions.SummaryResources ?? [];

  return (
    <section>
      <SectionHeader icon={AlertCircle} title="Restrictions" />

      <div className="mt-2 space-y-3">
        {/* Boolean flags grid */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 @md:grid-cols-3">
          {booleanFlags.map(({ label, value }) => (
            <div key={label} className="flex items-center gap-2 text-xs">
              <span
                className={cn(
                  'size-2 shrink-0 rounded-full',
                  value ? 'bg-amber-500' : 'bg-muted-foreground/30'
                )}
              />
              <span className={cn(value ? 'font-medium' : 'text-muted-foreground')}>{label}</span>
            </div>
          ))}
        </div>

        {/* Gender restriction */}
        {restrictions.GenderRestriction != null && restrictions.GenderRestriction !== 'Unknown' ? (
          <KeyValue label="Gender Restriction" value={restrictions.GenderRestriction} />
        ) : null}

        {/* Age limits */}
        {ageLimits.length > 0 ? (
          <DetailBlock title="Age Limits">
            {ageLimits.map((limit, idx) => (
              <AgeLimitRow key={`age-${idx}`} limit={limit} />
            ))}
          </DetailBlock>
        ) : null}

        {/* Quantity limits */}
        {quantityLimits.length > 0 ? (
          <DetailBlock title="Quantity Limits">
            {quantityLimits.map((limit, idx) => (
              <QuantityLimitRow key={`qty-${idx}`} limit={limit} />
            ))}
          </DetailBlock>
        ) : null}

        {/* Step therapy medications */}
        {stepMeds.length > 0 ? (
          <DetailBlock title="Step Therapy Medications" icon={ListTree}>
            <ol className="list-decimal space-y-1 pl-4">
              {[...stepMeds]
                .sort((a, b) => (a.StepOrder ?? 0) - (b.StepOrder ?? 0))
                .map((step, idx) => (
                  <StepMedRow key={step.Ndc ?? `step-${idx}`} step={step} />
                ))}
            </ol>
          </DetailBlock>
        ) : null}

        {/* Messages */}
        {messages.length > 0 ? (
          <DetailBlock title="Coverage Messages" icon={MessageSquare}>
            <div className="space-y-2">
              {messages.map((msg, idx) => (
                <MessageRow key={`msg-${idx}`} message={msg} />
              ))}
            </div>
          </DetailBlock>
        ) : null}

        {/* Drug resources */}
        {drugResources.length > 0 ? (
          <DetailBlock title="Drug Resources" icon={FileText}>
            <ResourcesList resources={drugResources} />
          </DetailBlock>
        ) : null}

        {/* Summary resources */}
        {summaryResources.length > 0 ? (
          <DetailBlock title="Plan Summary Resources" icon={FileText}>
            <ResourcesList resources={summaryResources} />
          </DetailBlock>
        ) : null}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Copays                                                            */
/* ------------------------------------------------------------------ */

function CopaysDetail({ copays }: { copays: PayerCopayT[] | null | undefined }) {
  if (!copays || copays.length === 0) {
    return (
      <EmptySection icon={Store} title="Copays">
        No copay data available.
      </EmptySection>
    );
  }

  return (
    <section>
      <SectionHeader icon={Store} title="Copays" />
      <div className="mt-2 space-y-2">
        {copays.map((copay, idx) => (
          <CopayCard key={`copay-${idx}`} copay={copay} />
        ))}
      </div>
    </section>
  );
}

function CopayCard({ copay }: { copay: PayerCopayT }) {
  const summary = describeCopay(copay);
  const fields: Array<{ label: string; value: string | number | boolean | null | undefined }> = [
    {
      label: 'Drug Specific',
      value: copay.IsDrugSpecific === true ? 'Yes' : copay.IsDrugSpecific === false ? 'No' : null,
    },
    { label: 'Pharmacy Type', value: pharmacyTypeLabel(copay.PharmacyType ?? null) },
    {
      label: 'Flat Copay',
      value: copay.FlatCopayAmount != null ? `$${copay.FlatCopayAmount}` : null,
    },
    {
      label: 'Percent Copay',
      value: copay.PercentCopayAmount != null ? `${copay.PercentCopayAmount}%` : null,
    },
    {
      label: 'Flat First',
      value:
        copay.FlatCopayAmountFirst === true
          ? 'Yes'
          : copay.FlatCopayAmountFirst === false
            ? 'No'
            : null,
    },
    {
      label: 'Min Copay',
      value: copay.MinimumCopayAmount != null ? `$${copay.MinimumCopayAmount}` : null,
    },
    {
      label: 'Max Copay',
      value: copay.MaximumCopayAmount != null ? `$${copay.MaximumCopayAmount}` : null,
    },
    { label: 'Days Supply', value: copay.DaysSupply },
    { label: 'Copay Tier', value: copay.CopayTier },
    { label: 'Max Copay Tier', value: copay.MaximumCopayTier },
    {
      label: 'OOP Min',
      value: copay.OutOfPocketMinimum != null ? `$${copay.OutOfPocketMinimum}` : null,
    },
    {
      label: 'OOP Max',
      value: copay.OutOfPocketMaximum != null ? `$${copay.OutOfPocketMaximum}` : null,
    },
  ];

  const populatedFields = fields.filter((f) => f.value != null && f.value !== '');

  return (
    <div className="rounded-md border border-border/50 bg-background/40 p-3 dark:border-border/30 dark:bg-background/20">
      {summary ? <div className="mb-2 text-xs font-medium">{summary}</div> : null}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 @md:grid-cols-3">
        {populatedFields.map(({ label, value }) => (
          <div key={label}>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="text-xs font-medium">{String(value)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Payer information                                                 */
/* ------------------------------------------------------------------ */

function PayerDetail({ payer }: { payer: PayerInformationT | null | undefined }) {
  if (!payer) {
    return (
      <EmptySection icon={ShieldCheck} title="Payer Information">
        No payer information available.
      </EmptySection>
    );
  }

  const identifiers: Array<{ label: string; value: string | number | null | undefined }> = [
    { label: 'Payer Name', value: payer.PayerName },
    { label: 'Payer ID', value: payer.PayerId },
    { label: 'Plan Name', value: payer.PlanName },
    { label: 'Eligibility ID', value: payer.PatientEligibilityId },
    { label: 'Coverage ID', value: payer.CoverageId },
    { label: 'BIN', value: payer.BIN },
    { label: 'PCN', value: payer.PCN },
    { label: 'Formulary ID', value: payer.FormularyId },
    { label: 'Alternatives ID', value: payer.AlternativesId },
    { label: 'Copay ID', value: payer.CopayId },
    { label: 'Member ID', value: payer.MemberId },
  ];
  const populatedIds = identifiers.filter((f) => f.value != null && f.value !== '');

  const demographics = payer.Demographics;
  const eligibilities = payer.PharmacyEligibilities ?? [];

  return (
    <section>
      <SectionHeader icon={ShieldCheck} title="Payer Information" />

      <div className="mt-2 space-y-3">
        {populatedIds.length > 0 ? (
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 @md:grid-cols-3">
            {populatedIds.map(({ label, value }) => (
              <div key={label}>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  {label}
                </div>
                <div className="break-all text-xs font-medium">{String(value)}</div>
              </div>
            ))}
          </div>
        ) : null}

        {payer.DemographicChanges ? (
          <div className="rounded-md border border-amber-400/40 bg-amber-50/50 px-2.5 py-1.5 text-[11px] font-medium text-amber-800 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-200">
            Payer reported demographic changes
          </div>
        ) : null}

        {demographics ? (
          <DetailBlock title="Member Demographics" icon={User}>
            <DemographicsCard demographics={demographics} />
          </DetailBlock>
        ) : null}

        {eligibilities.length > 0 ? (
          <DetailBlock title="Pharmacy Eligibilities" icon={Store}>
            <div className="space-y-1">
              {eligibilities.map((elig, idx) => (
                <PharmacyEligibilityRow key={`elig-${idx}`} eligibility={elig} />
              ))}
            </div>
          </DetailBlock>
        ) : null}

        {payer.ErrorMessage ? <KeyValue label="Error" value={payer.ErrorMessage} /> : null}
        {payer.InfoMessage ? <KeyValue label="Info" value={payer.InfoMessage} /> : null}
      </div>
    </section>
  );
}

function DemographicsCard({
  demographics,
}: {
  demographics: NonNullable<PayerInformationT['Demographics']>;
}) {
  const nameParts = [
    demographics.FirstName,
    demographics.MiddleName,
    demographics.LastName,
    demographics.Suffix,
  ].filter(Boolean);
  const fullName = nameParts.length > 0 ? nameParts.join(' ') : null;
  const addr = demographics.Address;
  const addressLine = addr
    ? [addr.AddressLine1, addr.AddressLine2, addr.City, addr.State, addr.ZipCode]
        .filter(Boolean)
        .join(', ')
    : null;

  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
      {fullName ? <KVCell label="Name" value={fullName} span={2} /> : null}
      {demographics.DateOfBirth ? (
        <KVCell
          label="DOB"
          value={demographics.DateOfBirth.split('T')[0] ?? demographics.DateOfBirth}
        />
      ) : null}
      {demographics.Gender ? <KVCell label="Gender" value={demographics.Gender} /> : null}
      {addressLine ? <KVCell label="Address" value={addressLine} span={2} /> : null}
    </div>
  );
}

function PharmacyEligibilityRow({ eligibility }: { eligibility: PharmacyEligibilityT }) {
  return (
    <div className="flex items-center justify-between rounded-md border border-border/40 bg-background/40 px-2.5 py-1 text-xs dark:border-border/30 dark:bg-background/20">
      <span className="font-medium">{eligibility.PharmacyType ?? 'Unknown'}</span>
      <span className="flex items-center gap-2 text-muted-foreground">
        {eligibility.Name ? <span>{eligibility.Name}</span> : null}
        {eligibility.EligibilityStatus ? (
          <span
            className={cn(
              'rounded-full px-1.5 py-0.5 text-[10px] font-medium',
              eligibility.EligibilityStatus === 'Covered'
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
                : 'bg-muted text-muted-foreground'
            )}
          >
            {eligibility.EligibilityStatus}
          </span>
        ) : null}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Sub-component rows                                                */
/* ------------------------------------------------------------------ */

function AgeLimitRow({ limit }: { limit: AgeLimitRestrictionT }) {
  const fields: Array<{ label: string; value: string }> = [];
  if (limit.MinimumAge != null) {
    fields.push({
      label: 'Min Age',
      value: `${limit.MinimumAge} ${limit.MinimumAgeUnit?.toLowerCase() ?? 'years'}`,
    });
  }
  if (limit.MaximumAge != null) {
    fields.push({
      label: 'Max Age',
      value: `${limit.MaximumAge} ${limit.MaximumAgeUnit?.toLowerCase() ?? 'years'}`,
    });
  }

  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md border border-border/40 bg-background/40 px-2.5 py-1.5 dark:border-border/30 dark:bg-background/20">
      {fields.map(({ label, value }) => (
        <div key={label}>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
          <div className="text-xs font-medium">{value}</div>
        </div>
      ))}
    </div>
  );
}

function QuantityLimitRow({ limit }: { limit: QuantityLimitRestrictionT }) {
  const fields: Array<{ label: string; value: string | number }> = [];
  if (limit.MaximumAmount != null) fields.push({ label: 'Max Amount', value: limit.MaximumAmount });
  if (limit.AmountUnit) fields.push({ label: 'Unit', value: limit.AmountUnit });
  if (limit.TimePeriodUnit) fields.push({ label: 'Period', value: limit.TimePeriodUnit });
  if (limit.MaximumTimePeriodUnits != null)
    fields.push({ label: 'Max Period Units', value: limit.MaximumTimePeriodUnits });
  if (limit.StartDate) fields.push({ label: 'Start', value: limit.StartDate });
  if (limit.EndDate) fields.push({ label: 'End', value: limit.EndDate });

  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md border border-border/40 bg-background/40 px-2.5 py-1.5 @md:grid-cols-3 dark:border-border/30 dark:bg-background/20">
      {fields.map(({ label, value }) => (
        <div key={label}>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
          <div className="text-xs font-medium">{String(value)}</div>
        </div>
      ))}
    </div>
  );
}

function StepMedRow({ step }: { step: StepMedicationRestrictionT }) {
  return (
    <li className="text-xs">
      <span className="font-medium">{step.DisplayName ?? step.Ndc ?? '—'}</span>
      {step.Ndc ? (
        <span className="ml-1.5 font-mono text-[10px] text-muted-foreground">NDC {step.Ndc}</span>
      ) : null}
    </li>
  );
}

function MessageRow({ message }: { message: CoverageMessageT }) {
  return (
    <div className="space-y-1 rounded-md border border-border/40 bg-background/40 px-2.5 py-2 dark:border-border/30 dark:bg-background/20">
      {message.Message ? (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Short</div>
          <div className="text-xs">{message.Message}</div>
        </div>
      ) : null}
      {message.LongMessage ? (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Long</div>
          <div className="text-xs">{message.LongMessage}</div>
        </div>
      ) : null}
    </div>
  );
}

function ResourcesList({ resources }: { resources: CoverageResourceT[] }) {
  return (
    <ul className="space-y-1">
      {resources.map((resource, idx) => {
        if (!resource.Url?.trim()) return null;
        return (
          <li key={`res-${idx}`} className="text-xs">
            <a
              href={resource.Url}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 text-sky-700 underline-offset-2 hover:underline dark:text-sky-300"
            >
              {resource.ResourceType ?? 'Resource'}
              <ExternalLink className="size-3" aria-hidden />
            </a>
            <span className="ml-2 break-all font-mono text-[10px] text-muted-foreground">
              {resource.Url}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/* ------------------------------------------------------------------ */
/*  Shared building blocks                                            */
/* ------------------------------------------------------------------ */

function SectionHeader({ icon: Icon, title }: { icon: typeof AlertCircle; title: string }) {
  return (
    <div className="flex items-center gap-2 border-b border-border/40 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-foreground/80 dark:border-border/30">
      <Icon className="size-3.5" aria-hidden />
      {title}
    </div>
  );
}

function EmptySection({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof AlertCircle;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <SectionHeader icon={Icon} title={title} />
      <div className="mt-2 text-xs text-muted-foreground">{children}</div>
    </section>
  );
}

function DetailBlock({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon?: typeof AlertCircle;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {Icon ? <Icon className="size-3" aria-hidden /> : null}
        {title}
      </div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function KeyValue({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-xs font-medium">{value}</div>
    </div>
  );
}

function KVCell({ label, value, span }: { label: string; value: string; span?: number }) {
  return (
    <div className={span === 2 ? 'col-span-2' : undefined}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-xs font-medium">{value}</div>
    </div>
  );
}
